/**
 * OFC Board Operations
 *
 * Board structure: front (3 cards), middle (5 cards), back (5 cards)
 * Provides functions for creating, modifying, and validating boards.
 */
import { OFCVariant, RowPosition } from './enums';
import { compareEntries, evaluateFrontHand, evaluateHighHand } from './hands';
import { addCardToRow, createEmptyRow, getRowCapacity, isRowComplete } from './row';
import { Board, Card, Placement, Row } from './types';
import { isDeuceToSevenVariant } from './variants';

// ============================================================================
// Board Creation
// ============================================================================

export function createEmptyBoard(playerIndex: number): Board {
  return {
    playerIndex,
    front: createEmptyRow(RowPosition.FRONT),
    middle: createEmptyRow(RowPosition.MIDDLE),
    back: createEmptyRow(RowPosition.BACK),
  };
}

// ============================================================================
// Row Access
// ============================================================================

export function getRow(board: Board, position: RowPosition): Row {
  return board[position];
}

export function setRow(board: Board, position: RowPosition, row: Row): Board {
  return { ...board, [position]: row };
}

// ============================================================================
// Board Queries
// ============================================================================

export function getBoardTotalCards(board: Board): number {
  return board.front.cards.length + board.middle.cards.length + board.back.cards.length;
}

export function isBoardComplete(board: Board): boolean {
  return isRowComplete(board.front) && isRowComplete(board.middle) && isRowComplete(board.back);
}

export function getAllBoardCards(board: Board): readonly Card[] {
  return [...board.front.cards, ...board.middle.cards, ...board.back.cards];
}

export function canPlaceOnRow(board: Board, position: RowPosition): boolean {
  return getRow(board, position).cards.length < getRowCapacity(position);
}

export function getAvailablePositions(board: Board): RowPosition[] {
  return [RowPosition.FRONT, RowPosition.MIDDLE, RowPosition.BACK].filter((p) => canPlaceOnRow(board, p));
}

// ============================================================================
// Card Placement (Immutable)
// ============================================================================

export function placeCardOnBoard(board: Board, card: Card, position: RowPosition, slotIndex?: number): Board {
  const row = getRow(board, position);
  const updatedRow = addCardToRow(row, card, slotIndex);
  return setRow(board, position, updatedRow);
}

export function placePlacementOnBoard(board: Board, placement: Placement): Board {
  return placeCardOnBoard(board, placement.card, placement.position, placement.slotIndex);
}

export function placePlacementsOnBoard(board: Board, placements: readonly Placement[]): Board {
  return placements.reduce((b, p) => placePlacementOnBoard(b, p), board);
}

// ============================================================================
// Foul Detection
// ============================================================================

interface FoulCheckResult {
  fouled: boolean;
  reason: string | null;
}

function checkBoardFoul(board: Board, variant?: OFCVariant): FoulCheckResult {
  if (!isBoardComplete(board)) {
    return { fouled: false, reason: null };
  }

  const middleEntry = evaluateHighHand(board.middle.cards);
  const backEntry = evaluateHighHand(board.back.cards);

  // Middle must not beat back
  if (compareEntries(middleEntry, backEntry) > 0) {
    return {
      fouled: true,
      reason: `Middle (${middleEntry.label}) beats Back (${backEntry.label})`,
    };
  }

  // In 2-7 variants, skip front vs middle check (middle is scored as lowball)
  if (variant && isDeuceToSevenVariant(variant)) {
    return { fouled: false, reason: null };
  }

  // Front must not beat middle
  const frontEntry = evaluateFrontHand(board.front.cards);
  const frontAs5Card = frontEntry.index * 100;
  if (frontAs5Card > middleEntry.index) {
    return {
      fouled: true,
      reason: `Front (${frontEntry.label}) beats Middle (${middleEntry.label})`,
    };
  }

  return { fouled: false, reason: null };
}

/**
 * Check if board is fouled (rows not in ascending strength order)
 */
export function isBoardFouled(board: Board, variant?: OFCVariant): boolean {
  return checkBoardFoul(board, variant).fouled;
}

/**
 * Get the reason why a board is fouled, or null if not fouled
 */
export function getFoulReason(board: Board, variant?: OFCVariant): string | null {
  return checkBoardFoul(board, variant).reason;
}
